import { useStoreSelector } from "design-system/store/store";
import { cloudinary } from "design-system/utils/cloudinary";
import { DataLayerEventData } from "frontend/hooks/use-data-layer";
import { ReactNode } from "react";
import { MediaAssetImageProps } from "../media-asset/media-asset-image";
import { getGoogleTagSource } from "frontend/lib/constants";

export type ArticleStructuredData = Partial<{
  headline: string;
  author: Array<{
    "@type": string;
    name: string;
    url?: string;
  }>;
  datePublished: string;
  image: string;
  articleSection: string;
  articleBody: string;
  url: string;
}>;

export type EventStructuredData = Partial<{
  name: string;
  startDate: string;
  endDate?: string;
  eventAttendanceMode?: string;
  eventStatus?: string;
  location: Array<object>;
  image?: string;
  description?: string;
  offers?: OfferStructuredData;
  performer?: object;
  organizer?: object;
}>;

export type EducationalOrganizationStructuredData = Partial<{
  "@id": string;
  "@type": "EducationalOrganization" | "CollegeOrUniversity";
  name: string;
  url?: string;
  address?: AddressStructuredData;
  sameAs?: Array<string>;
  breadcrumb?: BreadcrumbStructuredData;
  mainEntity?: FAQPageStructuredData;
}>;

export type EducationalOccupationalStructuredData = Partial<{
  "@type": "EducationalOccupationalProgram";
  "@id": string;
  name: string;
  description?: string;
  url?: string;
  provider?: EducationalOrganizationStructuredData;
  address?: AddressStructuredData;
  programType?: string;
  sameAs?: Array<string>;
  breadcrumb?: BreadcrumbStructuredData;
  teaches?: Array<string>;
  audience?: AudienceStructuredData;
  hasCourse?: Array<CourseStructuredData>;
}>;

export type AddressStructuredData = Partial<{
  "@type": string;
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
}>;

export type BreadcrumbStructuredData = Partial<{
  "@type": "BreadcrumbList";
  itemListElement: Array<{
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }>;
}>;

export type FAQPageStructuredData = Partial<{
  "@type": "FAQPage";
  mainEntity: Array<{
    "@type": "Question";
    name: string;
    acceptedAnswer: {
      "@type": "Answer";
      text: string;
    };
  }>;
}>;

export type BlogPageStructuredData = Partial<{
  "@graph": Array<BlogPostStructuredData | BreadcrumbStructuredData>;
}>;

export type BlogPostStructuredData = Partial<{
  "@type": "BlogPosting";
  mainEntityOfPage: string;
  headline: string;
  description: string;
  articleBody: string;
  author?: Array<{
    "@type": string;
    name: string;
    url?: string;
  }>;
  datePublished: string;
  publisher: {
    "@type": string;
    "@id": string;
    name: string;
    url: string;
  };
  image: string;
}>;

export type CourseStructuredData = Partial<{
  "@type": "Course";
  "@id"?: string;
  name: string;
  url?: string;
  description?: string;
  category?: Array<string>;
  provider?: EducationalOrganizationStructuredData;
  teaches?: Array<string>;
  audience?: AudienceStructuredData;
  hasCourseInstance?: Array<CourseInstanceStructuredData>;
  educationalProgram?: EducationalProgramStructuredData;
  offers?: OfferStructuredData;
}>;

export type AudienceStructuredData = Partial<{
  "@type": "Audience";
  audienceType: Array<string>;
}>;

export type CourseScheduleStructuredData = Partial<{
  "@type": "Schedule";
  startDate?: string;
  endDate?: string;
}>;

export type OfferStructuredData = Partial<{
  "@type": "Offer";
  url?: string;
  price?: number;
  priceCurrency?: string;
  itemCondition?: string;
  availability?: string;
}>;

export type CourseInstanceStructuredData = Partial<{
  "@type": "CourseInstance";
  "@id"?: string;
  name?: string;
  courseMode?: Array<string> | string;
  courseSchedule?: CourseScheduleStructuredData;
  startDate?: string;
  endDate?: string;
  location?: {
    "@type": string;
    name: string;
    address?: AddressStructuredData;
  };
  instructor?: Array<{
    "@type": string;
    name: string;
    jobTitle?: string;
    url?: string;
  }>;
}>;

export type EducationalProgramStructuredData = Partial<{
  "@type": "EducationalOccupationalProgram";
  "@id"?: string;
  name: string;
  url?: string;
  description?: string;
  provider?: EducationalOrganizationStructuredData;
  hasCourse?: Array<CourseStructuredData>;
}>;

export type PodcastStructuredData = Partial<{
  "@type": "PodcastEpisode";
  name: string;
  url?: string;
  description?: string;
  partOfSeries?: {
    "@type": "PodcastSeries";
    name: string;
    url?: string;
  };
  datePublished?: string;
  contentUrl?: string;
  publisher?: EducationalOrganizationStructuredData;
  image?: string;
  inLanguage?: string;
  associatedMedia?: {
    "@type": "MediaObject";
    contentUrl: string;
    encodingFormat?: string;
  };
}>;

export interface HeadProps {
  gtagId?: string;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  type?: string;
  siteName?: string;
  image?: MediaAssetImageProps;
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  twitterCreator?: string;
  twitterSite?: string;
  metaOverride?: Record<string, string | undefined>;
  children?: ReactNode;
  openGraphDescription?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  convertId?: string;
  pageLoadEventData?: DataLayerEventData;
  articleStructuredData?: ArticleStructuredData;
  blogPageStructuredData?: BlogPageStructuredData;
  eventStructuredData?: EventStructuredData;
  educationalOrganizationData?: EducationalOrganizationStructuredData;
  educationalOccupationalData?: EducationalOccupationalStructuredData;
  breadcrumbStructuredData?: BreadcrumbStructuredData;
  faqPageStructuredData?: FAQPageStructuredData;
  courseData?: CourseStructuredData;
  educationalProgramData?: EducationalProgramStructuredData;
  podcastData?: PodcastStructuredData;
  structuredData?: Array<Record<string, unknown>>;
}

export function Head({
  gtagId,
  title = "Harvard Business School",
  description = "Harvard Business School",
  siteName = "Harvard Business School",
  canonicalUrl,
  type = "website",
  image,
  twitterCard = "summary",
  twitterCreator = "@HarvardHBS",
  twitterSite = "@HarvardHBS",
  metaOverride,
  children,
  openGraphDescription,
  noFollow,
  noIndex,
  convertId,
  pageLoadEventData,
  articleStructuredData,
  blogPageStructuredData,
  eventStructuredData,
  educationalOccupationalData,
  educationalOrganizationData,
  courseData,
  educationalProgramData,
  podcastData,
  breadcrumbStructuredData,
  faqPageStructuredData,
  structuredData,
}: HeadProps) {
  const Container = useStoreSelector((store) => store.framework.Head);
  const imageSrc = cloudinary(image?.src);

  const meta = {
    "og:title": title,
    "og:description": openGraphDescription ?? description,
    "og:url": canonicalUrl,
    "og:type": type,
    "og:site_name": siteName,
    "og:image": imageSrc,
    "og:image:width": image?.width,
    "og:image:height": image?.height,
    "og:image:alt": image?.alt,
    "twitter:title": title,
    "twitter:description": description,
    "twitter:card": twitterCard,
    "twitter:creator": twitterCreator,
    "twitter:site": twitterSite,
    "twitter:image": imageSrc,
    "twitter:image:alt": image?.alt,
    ...metaOverride,
  };

  return (
    <Container>
      <link rel="preconnect" href="https://cloudinary.hbs.edu" />
      {noIndex && <meta key="robots-noindex" name="robots" content="noindex" />}
      {noFollow && (
        <meta key="robots-nofollow" name="robots" content="nofollow" />
      )}
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      {Object.entries(meta).map(
        ([property, value]) =>
          value && <meta property={property} content={value} key={property} />,
      )}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {convertId && (
        <script
          src={`https://cdn-4.convertexperiments.com/v1/js/${convertId}.js`}
        />
      )}
      {/* https://schema.org/breadcrumb */}
      {breadcrumbStructuredData && (
        <script
          key="breadcrumb-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...breadcrumbStructuredData,
            }),
          }}
        ></script>
      )}
      {/* https://schema.org/FAQPage */}
      {faqPageStructuredData && (
        <script
          key="faq-page-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...faqPageStructuredData,
            }),
          }}
        ></script>
      )}
      {/* https://schema.org/Blog */}
      {blogPageStructuredData && (
        <script
          key="blog-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...blogPageStructuredData,
            }),
          }}
        ></script>
      )}
      {/* https://developers.google.com/search/docs/appearance/structured-data/article */}
      {articleStructuredData && (
        <script
          key="article-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              publisher: {
                "@type": "Organization",
                name: "Harvard Business School",
              },

              url: articleStructuredData.url,
              headline: articleStructuredData.headline,
              image: articleStructuredData.image,
              articleBody: articleStructuredData.articleBody,
              datePublished: articleStructuredData.datePublished,
              articleSection: articleStructuredData.articleSection,
              author: articleStructuredData.author,
            }),
          }}
        ></script>
      )}
      {/* https://developers.google.com/search/docs/appearance/structured-data/event */}
      {eventStructuredData && (
        <script
          key="event-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Event",
              name: eventStructuredData.name,
              startDate: eventStructuredData.startDate,
              endDate: eventStructuredData.endDate,
              eventAttendanceMode: eventStructuredData.eventAttendanceMode,
              eventStatus: eventStructuredData.eventStatus,
              location: eventStructuredData.location,
              image: eventStructuredData.image,
              description: eventStructuredData.description,
              offers: eventStructuredData.offers,
              organizer: eventStructuredData.organizer,
            }),
          }}
        ></script>
      )}
      {/* https://developers.google.com/search/docs/appearance/structured-data/organization */}
      {educationalOccupationalData && (
        <script
          key="educational-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...educationalOccupationalData,
            }),
          }}
        ></script>
      )}
      {educationalOrganizationData && (
        <script
          key="organization-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "EducationalOrganization",
              name: educationalOrganizationData.name,
              url: educationalOrganizationData.url,
              address: educationalOrganizationData.address,
              sameAs: educationalOrganizationData.sameAs,
              mainEntity: educationalOrganizationData.mainEntity,
            }),
          }}
        ></script>
      )}
      {educationalOrganizationData?.breadcrumb && (
        <script
          key="organization-breadcrumb-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...educationalOrganizationData.breadcrumb,
            }),
          }}
        ></script>
      )}
      {/* https://developers.google.com/search/docs/appearance/structured-data/course-info (currently used for HBS Online)
      Even though “Course Info” is being deprecated, it’s still valuable for us to maintain it. While it doesn’t generate rich results, it does provide additional context for LLMs and AI search.*/}
      {courseData && (
        <script
          key="course-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Course",
              "@id": courseData["@id"],
              name: courseData.name,
              description: courseData.description,
              provider: courseData.provider,
              teaches: courseData.teaches,
              audience: courseData.audience,
              hasCourseInstance: courseData.hasCourseInstance,
              // educationalProgram: courseData.educationalProgram,
              offers: courseData.offers,
            }),
          }}
        ></script>
      )}

      {educationalProgramData && (
        <script
          key="educational-program-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "EducationalOccupationalProgram",
              ...educationalProgramData,
            }),
          }}
        ></script>
      )}

      {podcastData && (
        <script
          key="podcast-structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "PodcastEpisode",
              name: podcastData.name,
              url: podcastData.url,
              description: podcastData.description,
              partOfSeries: podcastData.partOfSeries,
              datePublished: podcastData.datePublished,
              contentUrl: podcastData.contentUrl,
              publisher: podcastData.publisher,
              image: podcastData.image,
              inLanguage: podcastData.inLanguage,
              associatedMedia: podcastData.associatedMedia,
            }),
          }}
        ></script>
      )}

      {structuredData?.map((data, i) => (
        <script
          key={`structured-data-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...data,
            }).replace(/</g, "\\u003c"),
          }}
        ></script>
      ))}

      {/* https://github.com/harvard-hbs/analytics-contentful-cms-data-layers/blob/2a2c659dd844557f1d42dc7ad162dc06ac49bca9/global/Page%20Load.md */}
      {pageLoadEventData && (
        <script
          key="gtm-init"
          dangerouslySetInnerHTML={{
            __html: `
                window.dataLayer = window.dataLater|| [];
                window.dataLayer.push({ event: "page_load", event_data: ${JSON.stringify(
                  pageLoadEventData,
                )} });`,
          }}
        />
      )}

      {/* https://developers.google.com/tag-platform/tag-manager/datalayer#installation */}
      {gtagId && (
        <script
          key="gtm-script"
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              '${getGoogleTagSource(gtagId)}'+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtagId}');
            `,
          }}
        />
      )}
      {children}
    </Container>
  );
}
